import {
  AdviceProductDetailsFormValues,
  CartAdviceValues,
} from "Components/Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import { MainPath, Uri } from "Constants/constants";
import { CART_ID_PARAM_ID, PRODUCT_ID_PARAM_ID } from "Constants/queryParams";
import {
  AddProduct,
  AddProductToCartData,
  OperationType,
} from "Store/addProductToCart/interfaces";
import {
  adviceDetailsFormValuesSelector,
  adviceSelector,
  cartAdviceSelector,
} from "Store/advice/stateSelectors";
import { advicePDF, akaAdvicePDF } from "Store/advicePDF/sagas";
import { CART_RESOURCE_ROOT, OPEN_CART_RESOURCE_ROOT } from "Utils/apiUtils";
import { createRequest, getError } from "Utils/requestUtils";
import { call, put, select, takeLatest } from "redux-saga/effects";
import {
  addAdviceProductsToCartStart,
  addAkaAdviceProductsToCartStart,
  addProductToCartSucceeded,
  addProductsToCartStart,
  operationFailed,
} from "./slice";

import { mapAdvicePriceCalculationResultToAddProduct } from "Components/Features/CheckPrice/Products/Advice/mappers/mapToCommerceAdvice";
import { ChildQueryParamNames } from "Components/Features/CheckPrice/checkPriceTypes";
import history from "Router/history";
import { AdviceState } from "Store/advice/interfaces";
import { handleCartAdviceUpdateRequest } from "Store/advice/sagas";
import { AdviceType } from "Store/advicePDF/interfaces";
import { isAdvisorSelector } from "Store/advisor/advisorSelectors";
import {
  advicePriceCalculationResultsStateSelector,
  adviceTypeSelector,
} from "Store/akaAdvice/akaAdviceSelectors";
import { AdvicePriceCalculationResult } from "Store/akaAdvice/interfaces/AkaAdviceState";
import { mapToCartProducts } from "Store/akaAdvice/mappers/mapToCartProducts/mapToCartProducts";
import { handleCartAkaAdviceUpdateRequest } from "Store/akaAdvice/sagas";
import { productAddedToCart } from "Store/analytics/analyticsSlice";
import { fetchCartStart } from "Store/cart/cartSlice";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { ChildProduct } from "Store/interfaces/products/checkout/lifeProducts/ChildProduct";
import { PartnerState } from "Store/partners/partnerInterfaces";
import { partnerIdSelector } from "Store/partners/partnerSelectors";
import { PriceCalculationState } from "Store/priceCalculation/priceCalculationInterfaces";
import { storedChildAndChildHealthProduct } from "Store/product/productUtils";
import { ReduxState } from "Store/rootReducer";
import { UserIdentificationData } from "Store/userIdentification";
import { userIdentificationSelector } from "Store/userIdentification/userIdentificationSelectors";
import { isDefined } from "Utils/functions";
import { getCampaignAndLeadQueryParams } from "Utils/navigation/queryParameterUtils";
import { pushToHistory } from "Utils/navigation/routerUtils";
import { mapFormToProduct } from "./productMappers";

interface MappedProducts {
  [key: string]: AddProductToCartData;
}

export const partnerSelector = (state: ReduxState): PartnerState =>
  state.partner;

const handleRedirectToCheckoutOrUpsell = (
  isUpsellActive: boolean,
  cartId: string,
): void => {
  const queryParams = new URLSearchParams(history.location.search);
  if (
    queryParams.has(CART_ID_PARAM_ID) &&
    queryParams.has(PRODUCT_ID_PARAM_ID)
  ) {
    queryParams.delete(CART_ID_PARAM_ID);
    queryParams.delete(PRODUCT_ID_PARAM_ID);
  }
  if (queryParams.has(ChildQueryParamNames.CHILD_INDEX)) {
    queryParams.delete(ChildQueryParamNames.CHILD_INDEX);
  }

  if (isUpsellActive) {
    pushToHistory(`/${MainPath.UPSELL}/${cartId}`, queryParams);
  } else {
    pushToHistory(`/${MainPath.OFFER}/${cartId}`, queryParams);
  }
};

export function* addProductsToCart({
  payload,
}: ReturnType<typeof addProductsToCartStart>): any {
  const partner: PartnerState = yield select(partnerSelector);
  const { customerData } = payload;

  if (!isDefined(customerData)) {
    throw new Error("No customer data present in payload");
  }

  const { products, storedProduct, ...rest } = payload;
  const mappedProducts: MappedProducts[] = products.map((product) =>
    mapFormToProduct(partner.partnerId, product.type, {
      ...product,
      ...rest,
    } as AddProductToCartData),
  );

  const cartId = customerData.newCartResponse.cartId;
  const isChildAndHealthProduct = storedChildAndChildHealthProduct(
    storedProduct,
    mappedProducts.length === 2,
  );

  if (isChildAndHealthProduct) {
    const storedChildProduct = storedProduct as ChildProduct;
    const relatedChildHealthProductId =
      storedChildProduct.relatedChildHealthProductId;

    if (!relatedChildHealthProductId) {
      yield addChildHealthAndUpdateChildProduct(
        payload.isUpsellActive,
        mappedProducts,
        products,
        cartId,
        payload.productId,
      );
    } else {
      yield updateChildAndChildHealthProduct(
        payload.isUpsellActive,
        mappedProducts,
        cartId,
        relatedChildHealthProductId,
      );
    }
  } else {
    if (storedProduct) {
      yield updateProduct(
        payload.isUpsellActive,
        mappedProducts,
        cartId,
        payload.productId,
      );
    } else {
      yield addProduct(
        payload.isUpsellActive,
        mappedProducts,
        products,
        cartId,
      );
    }
  }
}

function* updateProduct(
  isUpsellActive: boolean,
  mappedProducts: MappedProducts[],
  cartId: string,
  productId?: string,
): any {
  try {
    yield call(createRequest, {
      method: "patch",
      requestBody: mappedProducts[0],
      url: `${OPEN_CART_RESOURCE_ROOT}/${Uri.PRODUCTS}/${productId}`,
    });
    yield call(handleResponsesAfterAddProductToCart, isUpsellActive, cartId);
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.UPDATE_PRODUCT_REQUEST,
        response: getError(e),
      }),
    );
  }
}

function* addChildHealthAndUpdateChildProduct(
  isUpsellActive: boolean,
  mappedProducts: MappedProducts[],
  addedProducts: AddProduct[],
  cartId: string,
  productId?: string,
): any {
  const childProduct = mappedProducts.filter((product) => {
    return (
      (product.name as unknown as AddProductToCartData["type"]) ===
      INSURANCE_NAME_VALUE.NEW_CHILD
    );
  });
  const childHealthProduct = mappedProducts.filter((product) => {
    return (
      (product.name as unknown as AddProductToCartData["type"]) ===
      INSURANCE_NAME_VALUE.CHILD_HEALTH
    );
  });

  const mappedChildHealthProduct = childHealthProduct.map((product) => ({
    ...product,
    productId: "",
  }));

  try {
    const cartsOpenEndpoint = `${OPEN_CART_RESOURCE_ROOT}/${cartId}`;
    const productsUrl = `${cartsOpenEndpoint}/${Uri.PRODUCTS}`;

    yield call(createRequest, {
      method: "post",
      requestBody: { products: mappedChildHealthProduct },
      url: productsUrl,
      params: getCampaignAndLeadQueryParams(),
    });
    yield put(productAddedToCart(addedProducts));
    yield call(createRequest, {
      method: "patch",
      requestBody: childProduct[0],
      url: `${OPEN_CART_RESOURCE_ROOT}/${Uri.PRODUCTS}/${productId}`,
    });
    yield call(handleResponsesAfterAddProductToCart, isUpsellActive, cartId);
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.UPDATE_PRODUCT_REQUEST,
        response: getError(e),
      }),
    );
  }
}

function* updateChildAndChildHealthProduct(
  isUpsellActive: boolean,
  mappedProducts: MappedProducts[],
  cartId: string,
  childHealthProductId?: string,
): any {
  const childProduct = mappedProducts.filter((product) => {
    return (
      (product.name as unknown as AddProductToCartData["type"]) ===
      INSURANCE_NAME_VALUE.NEW_CHILD
    );
  });
  const childHealthProduct = mappedProducts.filter((product) => {
    return (
      (product.name as unknown as AddProductToCartData["type"]) ===
      INSURANCE_NAME_VALUE.CHILD_HEALTH
    );
  });

  const mappedChildHealthProduct = childHealthProduct.map((product) => ({
    ...product,
    productId: childHealthProductId,
  }));

  try {
    yield call(createRequest, {
      method: "patch",
      requestBody: childProduct[0],
      url: `${OPEN_CART_RESOURCE_ROOT}/${Uri.PRODUCTS}/${childProduct[0].productId}`,
    });
    yield call(createRequest, {
      method: "patch",
      requestBody: mappedChildHealthProduct[0],
      url: `${OPEN_CART_RESOURCE_ROOT}/${Uri.PRODUCTS}/${childHealthProductId}`,
    });
    yield call(handleResponsesAfterAddProductToCart, isUpsellActive, cartId);
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.UPDATE_PRODUCT_REQUEST,
        response: getError(e),
      }),
    );
  }
}

function* addProduct(
  isUpsellActive: boolean,
  mappedProducts: MappedProducts[],
  addedProducts: AddProduct[],
  cartId: string,
): any {
  try {
    const cartsOpenEndpoint = `${OPEN_CART_RESOURCE_ROOT}/${cartId}`;
    const productsUrl = `${cartsOpenEndpoint}/${Uri.PRODUCTS}`;

    yield call(createRequest, {
      method: "post",
      requestBody: { products: mappedProducts },
      url: productsUrl,
      params: getCampaignAndLeadQueryParams(),
    });
    yield put(productAddedToCart(addedProducts));
    yield call(handleResponsesAfterAddProductToCart, isUpsellActive, cartId);
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.ADD_PRODUCTS_REQUEST,
        response: getError(e),
      }),
    );
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* addAdviceProductsToCart({
  payload,
}: ReturnType<typeof addAdviceProductsToCartStart>) {
  const isAdvisor: boolean = yield select(isAdvisorSelector);
  const cartAdvice: CartAdviceValues | undefined = yield select(
    cartAdviceSelector,
  );
  const priceCalculation: PriceCalculationState = yield select(
    (state: ReduxState): PriceCalculationState => state.priceCalculation,
  );
  const userIdentification: UserIdentificationData = yield select(
    userIdentificationSelector,
  );

  if (userIdentification === undefined) {
    throw new Error("No user data");
  }

  const advice: AdviceState = yield select(adviceSelector);
  const advicePriceCalculations =
    advice.advicePriceCalculationResults.current ?? [];
  const adviceProducts: AddProduct[] =
    mapAdvicePriceCalculationResultToAddProduct(
      advicePriceCalculations,
      cartAdvice?.adviceProducts ?? [],
      payload.formProps,
      isAdvisor,
    );

  const partner: PartnerState = yield select(partnerSelector);
  const productsToBeAddedToCart = adviceProducts.map((product) =>
    mapFormToProduct(partner.partnerId, product.type, {
      ...product,
      customerData: userIdentification,
      formData: priceCalculation.priceCalculationFormStates,
    }),
  );

  try {
    yield call(advicePDF, {
      type: "advicePDF/advicePDFStart",
      payload: {
        ref: payload.screenshotRef,
        throwNewError: true,
        initialAdvice: isAdvisor,
      },
    });
    const shouldCloseAdvisorProductTab = isAdvisor && !payload.takeScreenshot;

    yield call(handleCartAdviceUpdateRequest);
    yield call(createRequest, {
      method: "post",
      requestBody: productsToBeAddedToCart,
      url: `${CART_RESOURCE_ROOT}/${userIdentification.newCartResponse.cartId}/adviceProducts`,
      open: false,
    });

    yield call(
      handleResponsesAfterAddProductToCart,
      false,
      userIdentification.newCartResponse.cartId,
      payload.redirectToCheckout,
      shouldCloseAdvisorProductTab,
      payload.onAdviceCompletedForAdvisor,
    );
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.ADD_ADVICE_PRODUCTS_REQUEST,
        response: getError(e),
      }),
    );
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* addAkaAdviceProductsToCart({
  payload,
}: ReturnType<typeof addAkaAdviceProductsToCartStart>) {
  const isAdvisor: boolean = yield select(isAdvisorSelector);
  const partnerId: PartnerState["partnerId"] = yield select(partnerIdSelector);
  const prices: AdvicePriceCalculationResult[] = yield select(
    advicePriceCalculationResultsStateSelector,
  );
  const userIdentification: UserIdentificationData = yield select(
    userIdentificationSelector,
  );
  const detailsFormValues: AdviceProductDetailsFormValues = yield select(
    adviceDetailsFormValuesSelector,
  );

  if (!isDefined(userIdentification)) {
    throw new Error("No user data");
  }

  const productsToBeAddedToCart = mapToCartProducts(isAdvisor, partnerId, {
    detailsFormData: detailsFormValues,
    priceFormData: payload.formProps,
    priceData: prices,
    customerData: userIdentification,
  });

  try {
    const adviceType: AdviceType = yield select(adviceTypeSelector);
    yield call(akaAdvicePDF, {
      type: "advicePDF/akaAdvicePDFStart",
      payload: {
        ref: payload.screenshotRef,
        throwNewError: true,
        initialAdvice: isAdvisor,
        adviceType,
      },
    });
    const shouldCloseAdvisorProductTab = isAdvisor && !payload.takeScreenshot;

    yield call(handleCartAkaAdviceUpdateRequest, payload.formProps);
    yield call(createRequest, {
      method: "post",
      requestBody: productsToBeAddedToCart,
      url: `${CART_RESOURCE_ROOT}/${userIdentification.newCartResponse.cartId}/adviceProducts`,
      open: false,
    });

    yield call(
      handleResponsesAfterAddProductToCart,
      false,
      userIdentification.newCartResponse.cartId,
      payload.redirectToCheckout,
      shouldCloseAdvisorProductTab,
      payload.onAdviceCompletedForAdvisor,
    );
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.ADD_ADVICE_PRODUCTS_REQUEST,
        response: getError(e),
      }),
    );
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* handleResponsesAfterAddProductToCart(
  isUpsellActive: boolean,
  cartId: string,
  redirectToCheckout = true,
  shouldCloseAdvisorProductTab = false,
  onAdviceCompletedForAdvisor?: (shouldCloseProductTab: boolean) => void,
) {
  yield put(addProductToCartSucceeded());
  const shouldHandleForAdvisor =
    (!redirectToCheckout || shouldCloseAdvisorProductTab) &&
    !!onAdviceCompletedForAdvisor;

  if (shouldHandleForAdvisor) {
    if (!shouldCloseAdvisorProductTab) {
      yield put(fetchCartStart({ cartId, forceRecalculate: true }));
    }
    yield call(onAdviceCompletedForAdvisor, shouldCloseAdvisorProductTab);
  } else if (redirectToCheckout) {
    yield call(handleRedirectToCheckoutOrUpsell, isUpsellActive, cartId);
  } else {
    yield put(fetchCartStart({ cartId, forceRecalculate: true }));
  }
}

export function* addProductToCartSaga(): Generator {
  yield takeLatest(addProductsToCartStart, addProductsToCart);
  yield takeLatest(addAdviceProductsToCartStart, addAdviceProductsToCart);
  yield takeLatest(addAkaAdviceProductsToCartStart, addAkaAdviceProductsToCart);
}
